<template>
  <div id="app">
    <!-- 登录页 -->
    <div v-if="isLoginPage" class="login-page">
      <router-view />
    </div>
    
    <!-- 主框架 -->
    <div v-else class="layout-container">
      <!-- 顶部导航 -->
      <header class="header">
        <div class="logo">
          <i :class="collapsed ? 'el-icon-s-unfold collapse-btn' : 'el-icon-s-fold collapse-btn'" @click="collapsed = !collapsed"></i>
          <div class="logo-icon">CCC</div>
          <span v-show="!collapsed">常畅充权益系统 v1.0</span>
        </div>
        <div class="header-right">
          <el-popover
            placement="bottom-end"
            width="380"
            trigger="click"
            v-model="noticeVisible"
            popper-class="notice-popover">
            <div class="notice-header">
              <span class="notice-title">系统公告</span>
              <el-button type="text" size="mini" @click="goNoticeManage">管理公告</el-button>
            </div>
            <div class="notice-body" v-loading="noticeLoading">
              <div v-if="!noticeList.length" class="notice-empty">暂无公告</div>
              <div v-else class="notice-list">
                <div v-for="item in noticeList" :key="item.id" class="notice-item" @click="viewNotice(item)">
                  <div class="notice-item-title">{{ item.title }}</div>
                  <div class="notice-item-time">{{ formatNoticeTime(item.publishTime || item.createdAt) }}</div>
                </div>
              </div>
            </div>
            <el-badge :value="notifications" :hidden="notifications === 0" slot="reference" class="item">
              <i class="el-icon-bell" style="color:#fff;font-size:18px;cursor:pointer"></i>
            </el-badge>
          </el-popover>
          <el-dropdown>
            <span class="user-info">
              <el-avatar :size="32" style="background:#fff;color:#1890ff;">A</el-avatar>
              <span>{{ currentUser }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item disabled>
                <i class="el-icon-user"></i> {{ currentUser }}
              </el-dropdown-item>
              <el-dropdown-item divided @click.native="logout">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>
      
      <!-- 主体区域 -->
      <div class="main-wrapper">
        <!-- 左侧导航：由 sys_menu 表(菜单设置页唯一数据源)动态渲染，并按当前管理员角色菜单权限过滤 -->
        <aside class="sidebar" :class="{collapsed}" :style="{width: collapsed ? '64px' : '220px'}">
          <el-menu
            :default-active="activeMenu"
            :collapse="collapsed"
            background-color="#001529"
            text-color="rgba(255,255,255,0.65)"
            active-text-color="#fff"
            router
          >
            <template v-for="m in visibleMenus">
              <!-- 无子菜单：直接作为菜单项 -->
              <el-menu-item v-if="!m.children || m.children.length === 0" :key="m.id" :index="m.path">
                <i :class="m.icon"></i>
                <span>{{ m.menuName }}</span>
              </el-menu-item>
              <!-- 有子菜单：作为分组 -->
              <el-submenu v-else :key="m.id" :index="m.path">
                <template slot="title">
                  <i :class="m.icon"></i>
                  <span>{{ m.menuName }}</span>
                </template>
                <el-menu-item v-for="c in m.children" :key="c.id" :index="c.path">
                  <i :class="c.icon"></i>
                  <span>{{ c.menuName }}</span>
                </el-menu-item>
              </el-submenu>
            </template>
          </el-menu>
        </aside>
        
        <!-- 内容区 -->
        <main class="main-content">
          <router-view />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { sysMenuMyMenus } from '@/api/sysMenu'
import { sysNoticeList } from '@/api/sysNotice'
import { menuState, loadMenuTree } from '@/store/menuStore'

export default {
  name: 'App',
  data() {
    return {
      collapsed: false,
      notifications: 0,
      noticeVisible: false,
      noticeList: [],
      noticeLoading: false,
      // 当前管理员可访问的菜单 id 集合（仅叶子 id）；null 表示「全部可见」（默认/降级）
      allowedMenuIds: null
    }
  },
  computed: {
    isLoginPage() {
      return this.$route.path === '/login'
    },
    activeMenu() {
      return this.$route.path
    },
    currentUser() {
      const user = localStorage.getItem('admin_user')
      return user ? JSON.parse(user).username : '管理员'
    },
    // 由 sys_menu 表（菜单设置页的唯一数据源）构建菜单树，并按角色权限过滤。
    // 读取 menuState.tree（响应式），菜单名称被修改后会实时反映到侧边栏。
    visibleMenus() {
      const flat = menuState.tree || []
      if (!flat.length) return []
      // 按 parentId 组装树（节点复用，children 单独收集）
      const map = {}
      flat.forEach(m => { map[m.id] = Object.assign({}, m, { children: [] }) })
      const roots = []
      flat.forEach(m => {
        const node = map[m.id]
        if (m.parentId && map[m.parentId]) {
          map[m.parentId].children.push(node)
        } else {
          roots.push(node)
        }
      })
      const all = this.allowedMenuIds === null
      const set = all ? null : new Set(this.allowedMenuIds || [])
      const filterNode = (node) => {
        if (!node.children || node.children.length === 0) {
          return all || (set && set.has(node.id))
        }
        node.children = node.children.filter(filterNode)
        return node.children.length > 0
      }
      return roots
        .filter(filterNode)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    }
  },
  created() {
    // 登录页/未登录时不应请求需要鉴权的菜单接口，避免 403
    this.loadMenus()
    this.fetchNotices()
  },
  watch: {
    // 登录成功后从 /login 跳转到业务页面，需要重新加载菜单权限
    '$route'() {
      this.loadMenus()
    }
  },
  methods: {
    loadMenus() {
      // 仅在非登录页且已登录时才请求管理员菜单数据
      if (this.isLoginPage || !localStorage.getItem('admin_user')) {
        this.allowedMenuIds = null
        menuState.tree = []
        return
      }
      // 拉取菜单数据（侧边栏数据源）
      loadMenuTree()
      // 拉取当前管理员按角色过滤后的菜单权限；失败/无授权时降级为全部可见
      sysMenuMyMenus().then(res => {
        if (res.data && res.data.code === 200) {
          this.allowedMenuIds = res.data.data // 可能为 null（全部可见）或 id 数组
        }
      }).catch(() => {
        this.allowedMenuIds = null
      })
    },
    logout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('admin_user')
        this.$message.success('已退出登录')
        this.$router.push('/login')
      }).catch(() => {})
    },
    // 拉取系统公告列表（仅已发布），用于铃铛角标+下拉展示
    fetchNotices() {
      if (this.isLoginPage || !localStorage.getItem('admin_user')) return
      this.noticeLoading = true
      sysNoticeList().then(res => {
        if (res.data && res.data.code === 200) {
          const list = (res.data.data || []).filter(n => n.status === 1)
          this.noticeList = list.slice(0, 10) // 最多展示 10 条
          this.notifications = list.length
        }
      }).catch(() => {}).finally(() => { this.noticeLoading = false })
    },
    // 点击单条公告：弹出详情
    viewNotice(item) {
      this.$alert(item.content || '暂无内容', item.title, {
        dangerouslyUseHTMLString: false,
        confirmButtonText: '知道了'
      })
    },
    // 跳转公告管理页
    goNoticeManage() {
      this.noticeVisible = false
      this.$router.push('/system/notice')
    },
    formatNoticeTime(t) {
      if (!t) return ''
      const s = String(t)
      return s.length >= 16 ? s.substring(0, 16).replace('T', ' ') : s
    }
  }
}
</script>

<style lang="scss">
// Element UI 主题色覆盖
$--color-primary: #52c41a;
$--color-success: #52c41a;
$--color-warning: #faad14;
$--color-danger: #ff4d4f;
$--color-info: #909399;

.login-page {
  min-height: 100vh;
}

.layout-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  height: 60px;
  background: linear-gradient(135deg, #88c788 0%, #76c576 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.logo {
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.logo-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.collapse-btn {
  font-size: 20px;
  cursor: pointer;
  color: #fff;
  margin-right: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.main-wrapper {
  flex: 1;
  display: flex;
}

.sidebar {
  background: #001529;
  color: rgba(255, 255, 255, 0.85);
  transition: width 0.3s;
  overflow: hidden;
  flex-shrink: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  
  &.collapsed {
    width: 64px !important;
  }
}

.el-menu {
  border-right: none !important;
  background-color: transparent !important;
  
  .el-submenu__title,
  .el-menu-item {
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    transition: all 0.3s ease;
  }
  
  .el-submenu__title:hover,
  .el-menu-item:hover {
    background: linear-gradient(90deg, rgba(82, 196, 26, 0.3) 0%, transparent 100%) !important;
    color: #fff !important;
  }
  
  .el-menu-item.is-active {
    background: linear-gradient(90deg, rgba(82, 196, 26, 0.4) 0%, rgba(82, 196, 26, 0.15) 100%) !important;
    color: #fff !important;
    font-weight: 500;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #73d13d;
      border-radius: 0 2px 2px 0;
    }
  }
  
  .el-submenu__icon-arrow {
    color: rgba(255, 255, 255, 0.5);
  }
  
  .el-icon-arrow-down {
    transition: transform 0.3s;
  }
  
  .is-opened > .el-submenu__title .el-icon-arrow-down {
    transform: rotate(180deg);
  }
}

.main-content {
  flex: 1;
  background: #f0f9eb;
  padding: 20px;
  overflow-y: auto;
}

/* 公告铃铛下拉 */
.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}
.notice-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}
.notice-body {
  max-height: 320px;
  overflow-y: auto;
}
.notice-empty {
  text-align: center;
  color: #999;
  padding: 30px 0;
  font-size: 13px;
}
.notice-list {}
.notice-item {
  padding: 10px 4px;
  cursor: pointer;
  border-bottom: 1px solid #fafafa;
  transition: background 0.2s;
}
.notice-item:hover {
  background: #f5f5f5;
  border-radius: 4px;
}
.notice-item-title {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notice-item-time {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>
